import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { fetchFeatureFlags, type FeatureFlagFromServer } from './api';

export const featureFlagsQueryKey = ['features', 'flags'] as const;

/**
 * Indexed view of all server-defined feature flags, keyed by ``flag_key``.
 * Hooks like ``useFeatureAccess`` consume this map to decide whether a
 * given UI surface should render and/or be enabled for the current user.
 *
 * Hooks return ``undefined`` for unknown keys so the caller can apply its
 * own fallback (the project default is "hide until known").
 */
export interface FeatureFlagsMap {
  byKey: Record<string, FeatureFlagFromServer>;
  isLoading: boolean;
  isError: boolean;
}

/**
 * Top-level hook that fetches the feature-flag catalog from
 * ``GET /api/v1/features/flags/`` and caches it. Used directly by
 * ``useFeatureAccess`` — most call sites should not need this.
 *
 * Cache strategy: 5-minute staleTime is plenty for flags that change at
 * deploy cadence, and we deliberately disable focus-refetch so toggling
 * tabs doesn't churn the UI.
 */
export function useFeatureFlags(): FeatureFlagsMap {
  const query = useQuery({
    queryKey: featureFlagsQueryKey,
    queryFn: fetchFeatureFlags,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: false,
    retry: 1,
  });

  return useMemo<FeatureFlagsMap>(() => {
    const byKey: Record<string, FeatureFlagFromServer> = {};
    for (const f of query.data ?? []) {
      byKey[f.flag_key] = f;
    }
    return {
      byKey,
      isLoading: query.isLoading,
      isError: query.isError,
    };
  }, [query.data, query.isLoading, query.isError]);
}
